import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import api from '../../../utils/authUtils';
import {
  Container, BackLink, Header, Title, Subtitle, Card, Button, Message,
  List, ListItem, Badge, Empty, Textarea, Field, Actions,
} from '../SpeakerManagement/ui';

const STATUS_META = {
  SUBMITTED: { bg: '#e0f2fe', fg: '#075985', id: 'submissions.status.submitted', def: 'Submitted' },
  UNDER_REVIEW: { bg: '#fef3c7', fg: '#92400e', id: 'submissions.status.underReview', def: 'Under review' },
  ACCEPTED: { bg: '#dcfce7', fg: '#166534', id: 'submissions.status.accepted', def: 'Accepted' },
  REJECTED: { bg: '#fee2e2', fg: '#991b1b', id: 'submissions.status.rejected', def: 'Rejected' },
  WITHDRAWN: { bg: '#f1f5f9', fg: '#64748b', id: 'submissions.status.withdrawn', def: 'Withdrawn' },
};
const OPEN_STATUSES = ['SUBMITTED', 'UNDER_REVIEW'];

const TextBlock = ({ label, value }) => value ? (
  <div style={{ marginBottom: '.9rem' }}>
    <div style={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '.3rem' }}>{label}</div>
    <div style={{ fontSize: '.9rem', color: '#1f2937', whiteSpace: 'pre-wrap' }}>{value}</div>
  </div>
) : null;

// Admin review of scientific submissions - open items surfaced here the same
// way Speaker Management surfaces pending title changes/slides: a banner at
// the top, filterable, one place to act on everything outstanding.
const SubmissionList = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [submissions, setSubmissions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [openOnly, setOpenOnly] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/api/cms/submissions');
      setSubmissions(data || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else setMessage({ error: true, text: t('submissionMgmt.loadError', 'Could not load submissions.') });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCount = submissions.filter(s => OPEN_STATUSES.includes(s.status)).length;
  const shown = openOnly ? submissions.filter(s => OPEN_STATUSES.includes(s.status)) : submissions;

  const toggle = s => {
    if (expanded === s.id) { setExpanded(null); return; }
    setExpanded(s.id);
    setDrafts(d => ({ ...d, [s.id]: d[s.id] || { internalNotes: s.internalNotes || '', notesToSubmitter: s.notesToSubmitter || '' } }));
  };

  const setDraft = (id, key, value) => setDrafts(d => ({ ...d, [id]: { ...d[id], [key]: value } }));

  const decide = async (s, status) => {
    setBusy(true);
    setMessage(null);
    const draft = drafts[s.id] || {};
    try {
      await api.put(`/api/cms/submissions/${s.id}/status`, {
        status, internalNotes: draft.internalNotes || '', notesToSubmitter: draft.notesToSubmitter || '',
      });
      await load();
      setMessage({ text: t('submissionMgmt.updated', 'Submission updated.') });
    } catch (err) {
      setMessage({ error: true, text: t('submissionMgmt.updateError', 'Could not update this submission.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container>
      <BackLink to="/admin/dashboard"><ArrowLeft size={16} /><FormattedMessage id="submissionMgmt.back" defaultMessage="Back to Dashboard" /></BackLink>
      <Header>
        <Title><FormattedMessage id="submissionMgmt.title" defaultMessage="Scientific Submissions" /></Title>
        <Button onClick={load}><RefreshCw size={16} /><FormattedMessage id="submissionMgmt.refresh" defaultMessage="Refresh" /></Button>
      </Header>
      <Subtitle><FormattedMessage id="submissionMgmt.subtitle" defaultMessage="Review scientific submissions from any registered user." /></Subtitle>
      {message && <Message error={message.error}>{message.text}</Message>}

      {openCount > 0 && (
        <Card style={{ borderLeft: '4px solid #92400e', background: '#fffbeb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#92400e" />
              <strong>
                <FormattedMessage id="submissionMgmt.banner"
                  defaultMessage="{count, plural, one {# submission is} other {# submissions are}} awaiting review."
                  values={{ count: openCount }} />
              </strong>
            </div>
            <Button onClick={() => setOpenOnly(o => !o)}>
              {openOnly
                ? <FormattedMessage id="submissionMgmt.showAll" defaultMessage="Show all" />
                : <FormattedMessage id="submissionMgmt.review" defaultMessage="Review now" />}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        {shown.length ? (
          <List>
            {shown.map(s => {
              const meta = STATUS_META[s.status] || { bg: '#f1f5f9', fg: '#334155', id: null, def: s.status };
              const isOpen = expanded === s.id;
              const draft = drafts[s.id] || { internalNotes: s.internalNotes || '', notesToSubmitter: s.notesToSubmitter || '' };
              const decidable = OPEN_STATUSES.includes(s.status);
              return (
                <ListItem key={s.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => toggle(s)}>
                    <div style={{ flex: 1, minWidth: '14rem' }}>
                      <div style={{ fontWeight: 700, color: '#111D4E' }}>{s.title}</div>
                      <div style={{ fontSize: '.82rem', color: '#64748b' }}>
                        {s.submitterName || s.submitterEmail} · {s.eventName}
                      </div>
                    </div>
                    <Badge style={{ background: meta.bg, color: meta.fg }}>{meta.id ? t(meta.id, meta.def) : meta.def}</Badge>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #eef2f7', paddingTop: '1rem' }}>
                      <TextBlock label={t('submissions.field.coAuthors', 'Co-authors')} value={s.coAuthors} />
                      <TextBlock label={t('submissions.field.phoneNumber', 'Phone number')} value={s.phoneNumber} />
                      <TextBlock label={t('submissionMgmt.field.language', 'Language')} value={s.language} />
                      <TextBlock label={t('submissions.field.background', 'Background')} value={s.backgroundText} />
                      <TextBlock label={t('submissions.field.methods', 'Methods')} value={s.methodsText} />
                      <TextBlock label={t('submissions.field.results', 'Results')} value={s.resultsText} />
                      <TextBlock label={t('submissions.field.conclusion', 'Conclusion')} value={s.conclusionText} />
                      <TextBlock label={t('submissions.field.heartsMatch', 'HEARTS MATCH')} value={s.heartsMatch} />

                      <Field>
                        {t('submissionMgmt.field.internalNotes', 'Internal notes (never shown to submitter)')}
                        <Textarea value={draft.internalNotes} onChange={e => setDraft(s.id, 'internalNotes', e.target.value)} />
                      </Field>
                      <Field style={{ marginTop: '.75rem' }}>
                        {t('submissionMgmt.field.notesToSubmitter', 'Note to submitter (sent with the decision)')}
                        <Textarea value={draft.notesToSubmitter} onChange={e => setDraft(s.id, 'notesToSubmitter', e.target.value)} />
                      </Field>

                      <Actions>
                        {decidable && s.status !== 'UNDER_REVIEW' && (
                          <Button secondary disabled={busy} onClick={() => decide(s, 'UNDER_REVIEW')}>
                            <FormattedMessage id="submissionMgmt.markUnderReview" defaultMessage="Mark under review" />
                          </Button>
                        )}
                        {decidable && (
                          <>
                            <Button disabled={busy} onClick={() => decide(s, 'ACCEPTED')}>
                              <FormattedMessage id="submissionMgmt.accept" defaultMessage="Accept" />
                            </Button>
                            <Button danger disabled={busy} onClick={() => decide(s, 'REJECTED')}>
                              <FormattedMessage id="submissionMgmt.reject" defaultMessage="Reject" />
                            </Button>
                          </>
                        )}
                        {!decidable && (
                          <Button secondary disabled={busy} onClick={() => decide(s, s.status)}>
                            <FormattedMessage id="submissionMgmt.saveNotes" defaultMessage="Save notes" />
                          </Button>
                        )}
                      </Actions>
                    </div>
                  )}
                </ListItem>
              );
            })}
          </List>
        ) : <Empty><FormattedMessage id="submissionMgmt.empty" defaultMessage="No submissions yet." /></Empty>}
      </Card>
    </Container>
  );
};

export default SubmissionList;
